import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import './Level2.css';
import { unlockNextLevel } from './Levelselect';

import Preloader from '../components/layout/Preloader';
import Header from '../components/layout/Header';
import PausePopup from '../components/layout/Pausepopup';
import WinPopup from '../components/layout/Winpopup';
import LosePopup from '../components/layout/Losepopup';
import level2Bg from '../asserts/level1bg.png';
import leftChar from '../asserts/avatar.png';
import rightChar from '../asserts/av5.png';
import fragmentImg from '../asserts/fragment5.png';
import stoneBack from '../asserts/stoneback.png';
import stone2 from '../asserts/stone2.png';
import stone3 from '../asserts/stone3.png';
import stone4 from '../asserts/stone4.png';
import stone5 from '../asserts/stone5.png';
import stone6 from '../asserts/stone6.png';
import stone7 from '../asserts/stone7.png';
import stone8 from '../asserts/stone8.png';
import stone9 from '../asserts/stone9.png';

const TIMER_SECONDS   = 80;
const SHUFFLE_AT      = 40;
const PREVIEW_SECONDS = 5;

const ALL_STONES = [stone2, stone3, stone4, stone5, stone6, stone7, stone8, stone9];

const shuffleArr = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const DIALOGUES = [
  { side: 'left',  text: '"The fifth fragment hides behind the stones. I will find it."' },
  { side: 'right', text: '"These stones hold ancient secrets. Watch them well before they vanish from sight."' },
];

const TASK2_POOL_FACTORY = (levelStones) => [
  {
    question: '"Among the seven sacred stones, one was touched by the gods themselves. Which carries the divine mark?"',
    hint: 'Hint: The gods left their seal on the stone shaped like the eye of the sky.',
    answers: [
      { stoneImg: levelStones[2], correct: true  },
      { stoneImg: levelStones[0], correct: false },
      { stoneImg: levelStones[4], correct: false },
    ],
  },
  {
    question: '"The high priest hid the fifth fragment beneath one stone. Which stone conceals the truth?"',
    hint: 'Hint: It bears the mark of the serpent that guards the underworld gate.',
    answers: [
      { stoneImg: levelStones[3], correct: true  },
      { stoneImg: levelStones[1], correct: false },
      { stoneImg: levelStones[5], correct: false },
    ],
  },
  {
    question: '"One of these stones was carved at the moment of the King\'s fall. Find the stone of sorrow."',
    hint: 'Hint: It was the last stone placed before the tomb was sealed forever.',
    answers: [
      { stoneImg: levelStones[6], correct: true  },
      { stoneImg: levelStones[0], correct: false },
      { stoneImg: levelStones[4], correct: false },
    ],
  },
];

const makeCards = (levelStones) => {
  const pairs = [0,1,2,3,4,5,6, 0,1,2,3,4,5,6];
  const arr = pairs.map((sym, i) => ({ id: i, symbol: sym, flipped: false, matched: false }));
  return shuffleArr(arr);
};

function StoneCard({ card, onClick, isChecking, showFront, levelStones }) {
  const show = showFront || card.flipped || card.matched;
  return (
    <div
      className={`stone-card ${show ? 'flipped' : ''} ${card.matched ? 'matched' : ''} ${isChecking ? 'no-click' : ''}`}
      onClick={() => !card.matched && !isChecking && !showFront && onClick(card.id)}
    >
      <div className="stone-inner">
        <div className="stone-front">
          <img src={levelStones[card.symbol]} alt="stone" />
        </div>
        <div className="stone-back">
          <img src={stoneBack} alt="stone back" />
        </div>
      </div>
    </div>
  );
}

function Task2({ onCorrect, onWrong, task2 }) {
  const [showWin, setShowWin] = useState(false);
  const answeredRef  = useRef(false);
  const completedRef = useRef(false);
  const overlayRef   = useRef();
  const msgRef       = useRef();
  const fragRef      = useRef();
  const boxRef       = useRef();
  const btnsRef      = useRef([]);

  useEffect(() => {
    const tl = gsap.timeline();
    if (boxRef.current) tl.fromTo(boxRef.current,
      { opacity: 0, y: 40, rotateX: -20 },
      { opacity: 1, y: 0, rotateX: 0, duration: 0.6, ease: 'back.out(1.8)' }
    );
    btnsRef.current.forEach((btn, i) => {
      if (btn) tl.fromTo(btn,
        { opacity: 0, scale: 0, rotation: -15 },
        { opacity: 1, scale: 1, rotation: 0, duration: 0.6, ease: 'elastic.out(1.2, 0.5)' },
        i === 0 ? '-=0.2' : '-=0.35'
      );
    });
  }, []);

  const handleAnswer = useCallback((correct) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    if (correct) setShowWin(true);
    else onWrong();
  }, [onWrong]);

  useEffect(() => {
    if (!showWin || completedRef.current) return;
    completedRef.current = true;
    const tl = gsap.timeline({ onComplete: () => setTimeout(() => onCorrect(), 1200) });
    if (overlayRef.current) tl.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.4 });
    if (msgRef.current) tl.fromTo(msgRef.current,
      { opacity: 0, y: -60, scale: 0.75 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(2)' }, '-=0.1'
    );
    if (fragRef.current) {
      tl.fromTo(fragRef.current,
        { opacity: 0, scale: 0, rotation: -25, y: 80 },
        { opacity: 1, scale: 1, rotation: 0, y: 0, duration: 0.85, ease: 'elastic.out(1,0.55)' }, '-=0.2'
      );
      tl.to(fragRef.current, { y: -18, duration: 1.1, ease: 'sine.inOut', yoyo: true, repeat: 3 }, '+=0.1');
    }
  }, [showWin, onCorrect]);

  return (
    <div className="task2-overlay">
      {!showWin ? (
        <>
          <div className="task2-question-box" ref={boxRef} style={{ opacity: 0 }}>
            <p className="task2-question-text">{task2.question}</p>
            <p className="task2-hint-text">{task2.hint}</p>
          </div>
          <div className="taskk2-answers">
            {task2.answers.map((ans, i) => (
              <button
                key={i}
                className="taskk2-answer-btn"
                ref={el => btnsRef.current[i] = el}
                style={{ opacity: 0 }}
                onClick={() => handleAnswer(ans.correct)}
              >
                <img src={ans.stoneImg} alt="answer" className="task2-stone-img" />
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="task2-win-overlay" ref={overlayRef} style={{ opacity: 0 }}>
          <div className="task2-win-msg" ref={msgRef} style={{ opacity: 0 }}>
            <p>"You have got it! The fifth fragment is yours."</p>
          </div>
          <div className="task2-win-fragment" ref={fragRef} style={{ opacity: 0 }}>
            <img src={fragmentImg} alt="Heart Fragment" />
          </div>
        </div>
      )}
    </div>
  );
}

const Level5 = () => {
  const navigate = useNavigate();

  const [levelStones] = useState(() => shuffleArr(ALL_STONES).slice(0, 7));
  const [task2] = useState(() => {
    const stones = shuffleArr(ALL_STONES).slice(0, 7);
    const pool = TASK2_POOL_FACTORY(stones);
    return pool[Math.floor(Math.random() * pool.length)];
  });

  const [showPreloader,  setShowPreloader]  = useState(true);
  const [introsDone,     setIntrosDone]     = useState(false);
  const [phase,          setPhase]          = useState('preview');
  const [previewLeft,    setPreviewLeft]    = useState(PREVIEW_SECONDS);
  const [cards,          setCards]          = useState(() => makeCards());
  const [flippedIds,     setFlippedIds]     = useState([]);
  const [isChecking,     setIsChecking]     = useState(false);
  const [timeLeft,       setTimeLeft]       = useState(TIMER_SECONDS);
  const [shuffled,       setShuffled]       = useState(false);
  const [showTask2,      setShowTask2]      = useState(false);
  const [showPause,      setShowPause]      = useState(false);
  const [showWinPopup,   setShowWinPopup]   = useState(false);
  const [showLosePopup,  setShowLosePopup]  = useState(false);

  const headerRef      = useRef();
  const hintRef        = useRef();
  const memorizeRef    = useRef();
  const timerRef       = useRef();
  const gridRef        = useRef();
  const task2Ref       = useRef();
  const intervalRef    = useRef(null);
  const previewRef     = useRef(null);
  const pauseRef       = useRef();
  const hintBounce     = useRef(null);
  const gameWonRef     = useRef(false);
  const isShufflingRef = useRef(false);

  const stopAllTimers = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (previewRef.current)  { clearInterval(previewRef.current);  previewRef.current  = null; }
  }, []);

  const animatedShuffleCards = useCallback(() => {
    if (isShufflingRef.current || !gridRef.current) return;
    isShufflingRef.current = true;
    const cardEls = Array.from(gridRef.current.querySelectorAll('.stone-card:not(.matched)'));
    if (cardEls.length === 0) { isShufflingRef.current = false; return; }
    const tl = gsap.timeline({ onComplete: () => { isShufflingRef.current = false; } });
    tl.to(cardEls, {
      scale: 0.6,
      rotation: () => (Math.random() - 0.5) * 40,
      x: () => (Math.random() - 0.5) * 120,
      y: () => (Math.random() - 0.5) * 80,
      opacity: 0,
      duration: 0.35,
      ease: 'power3.in',
      stagger: { amount: 0.2, from: 'random' },
    })
    .call(() => {
      setCards(prev => {
        const matched   = prev.filter(c => c.matched);
        const unmatched = prev.filter(c => !c.matched);
        for (let i = unmatched.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [unmatched[i], unmatched[j]] = [unmatched[j], unmatched[i]];
        }
        return [...matched, ...unmatched];
      });
    })
    .set(cardEls, { x: 0, y: 0, rotation: 0, scale: 0.5, opacity: 0 })
    .to(cardEls, {
      scale: 1, opacity: 1, rotation: 0, duration: 0.5, ease: 'back.out(1.8)',
      stagger: { amount: 0.25, from: 'random' },
    });
  }, []);

  const runIntroAnim = useCallback(() => {
    if (headerRef.current)   gsap.set(headerRef.current,   { opacity: 0, y: -60 });
    if (hintRef.current)     gsap.set(hintRef.current,     { opacity: 0, y: -40, scale: 0.88 });
    if (memorizeRef.current) gsap.set(memorizeRef.current, { opacity: 0, y: -30, scale: 0.88 });
    if (gridRef.current)     gsap.set(gridRef.current,     { opacity: 0, scale: 0.8, y: 60 });
    const tl = gsap.timeline();
    if (headerRef.current)
      tl.to(headerRef.current, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' });
    if (hintRef.current)
      tl.to(hintRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(2)' }, '-=0.3');
    if (memorizeRef.current)
      tl.to(memorizeRef.current, {
        opacity: 1, y: 0, scale: 1, duration: 0.7, ease: 'back.out(2.2)',
        onComplete: () => {
          if (memorizeRef.current) {
            hintBounce.current = gsap.to(memorizeRef.current, { y: -7, duration: 0.65, ease: 'sine.inOut', yoyo: true, repeat: -1 });
          }
        },
      }, '-=0.3');
    if (gridRef.current)
      tl.to(gridRef.current, { opacity: 1, scale: 1, y: 0, duration: 0.9, ease: 'back.out(1.5)' }, '-=0.3');
  }, []);

  useEffect(() => { if (introsDone) runIntroAnim(); }, [introsDone, runIntroAnim]);

  useEffect(() => {
    if (!introsDone || phase !== 'preview') return;
    previewRef.current = setInterval(() => {
      setPreviewLeft(p => {
        if (p <= 1) {
          clearInterval(previewRef.current);
          previewRef.current = null;
          if (hintBounce.current) { hintBounce.current.kill(); hintBounce.current = null; }
          const tl = gsap.timeline({
            onComplete: () => {
              if (timerRef.current) {
                gsap.fromTo(timerRef.current,
                  { opacity: 0, y: -30, scale: 0.85 },
                  { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: 'back.out(2)' }
                );
              }
            }
          });
          if (hintRef.current)     tl.to(hintRef.current,     { opacity: 0, y: -30, scale: 0.88, duration: 0.35, ease: 'power2.in' });
          if (memorizeRef.current) tl.to(memorizeRef.current, { opacity: 0, y: -30, scale: 0.88, duration: 0.35, ease: 'power2.in' }, '<');
          setCards(prev => prev.map(c => ({ ...c, flipped: false })));
          setPhase('play');
          intervalRef.current = setInterval(() => {
            setTimeLeft(t => {
              if (gameWonRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; return t; }
              if (t <= 1) { clearInterval(intervalRef.current); intervalRef.current = null; setShowLosePopup(true); return 0; }
              if (t - 1 === SHUFFLE_AT) {
                setShuffled(prev => { if (!prev) { setTimeout(animatedShuffleCards, 0); return true; } return prev; });
              }
              return t - 1;
            });
          }, 1000);
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => { if (previewRef.current) { clearInterval(previewRef.current); previewRef.current = null; } };
  }, [introsDone, phase, shuffled, animatedShuffleCards]);

  pauseRef.current = () => { stopAllTimers(); setShowPause(true); };

  const handleResume = () => {
    setShowPause(false);
    if (phase === 'preview') {
      previewRef.current = setInterval(() => {
        setPreviewLeft(p => {
          if (p <= 1) { clearInterval(previewRef.current); previewRef.current = null; return 0; }
          return p - 1;
        });
      }, 1000);
      return;
    }
    if (phase === 'play' && !gameWonRef.current) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (gameWonRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; return t; }
          if (t <= 1) { clearInterval(intervalRef.current); intervalRef.current = null; setShowLosePopup(true); return 0; }
          return t - 1;
        });
      }, 1000);
    }
  };

  const handleCardClick = useCallback((id) => {
    if (isChecking || phase !== 'play' || isShufflingRef.current) return;
    setFlippedIds(prev => {
      if (prev.includes(id) || prev.length >= 2) return prev;
      const next = [...prev, id];
      setCards(c => c.map(card => card.id === id ? { ...card, flipped: true } : card));
      if (next.length === 2) {
        setIsChecking(true);
        setTimeout(() => {
          setCards(c => {
            const a = c.find(card => card.id === next[0]);
            const b = c.find(card => card.id === next[1]);
            if (a && b && a.symbol === b.symbol) {
              const updated = c.map(card =>
                card.id === a.id || card.id === b.id ? { ...card, matched: true, flipped: true } : card
              );
              if (updated.every(card => card.matched)) {
                gameWonRef.current = true;
                stopAllTimers();
                setTimeout(() => {
                  if (gridRef.current) {
                    gsap.to(gridRef.current, {
                      opacity: 0, scale: 0.85, y: 40, duration: 0.5, ease: 'power2.in',
                      onComplete: () => setShowTask2(true),
                    });
                  } else setShowTask2(true);
                }, 600);
              }
              return updated;
            }
            return c.map(card => next.includes(card.id) ? { ...card, flipped: false } : card);
          });
          setFlippedIds([]);
          setIsChecking(false);
        }, 900);
      }
      return next;
    });
  }, [isChecking, phase, stopAllTimers]);

  useEffect(() => {
    if (!showTask2 || !task2Ref.current) return;
    gsap.fromTo(task2Ref.current,
      { opacity: 0, y: '100vh', scale: 0.85 },
      { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'expo.out' }
    );
  }, [showTask2]);

  useEffect(() => () => stopAllTimers(), [stopAllTimers]);

  const mins = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const secs = String(timeLeft % 60).padStart(2, '0');

  if (showPreloader) return <Preloader onComplete={() => setShowPreloader(false)} />;

  return (
    <div className="level2-page" style={{ backgroundImage: `url(${level2Bg})` }}>
      {!introsDone && (() => {
        const LevelIntro = require('../components/layout/Levelintro').default;
        return <LevelIntro leftImage={leftChar} rightImage={rightChar} dialogues={DIALOGUES} onFinish={() => setIntrosDone(true)} />;
      })()}

      <div ref={headerRef} style={{ opacity: 0, position: 'relative', zIndex: 100 }}>
        <Header mode="game" onPause={() => pauseRef.current && pauseRef.current()} />
      </div>

      {introsDone && !showTask2 && (
        <>
          <div className="hint-box" ref={hintRef} style={{ opacity: 0 }}>
            <p>"Seven pairs are hidden in the dark. Find them all before time runs out."</p>
          </div>
          <div className="memorize-box" ref={memorizeRef} style={{ opacity: 0 }}>
            {phase === 'preview'
              ? <p>Memorize the stones! <span className="preview-count">{previewLeft}s</span></p>
              : <p>Find the matching pairs!</p>
            }
          </div>
          <div className="timer-box" ref={timerRef} style={{ opacity: 0 }}>
            <span className={timeLeft <= 10 ? 'timer-urgent' : ''}>{mins}:{secs}</span>
          </div>
          <div className="cards-grid" ref={gridRef} style={{ opacity: 0 }}>
            {cards.map(card => (
              <StoneCard key={card.id} card={card} onClick={handleCardClick}
                isChecking={isChecking} showFront={phase === 'preview'} levelStones={levelStones} />
            ))}
          </div>
        </>
      )}

      {showTask2 && (
        <div ref={task2Ref} style={{ opacity: 0, position: 'relative', zIndex: 50 }}>
          <Task2
            task2={task2}
            onCorrect={() => { unlockNextLevel(5); if (!showWinPopup) setShowWinPopup(true); }}
            onWrong={() => { if (!showLosePopup) setShowLosePopup(true); }}
          />
        </div>
      )}

      {showPause && <PausePopup onResume={handleResume} onLevels={() => navigate('/levels')} onMenu={() => navigate('/mainmenu')} />}
      {showWinPopup && <WinPopup currentLevelId={5} fragmentImg={fragmentImg} onNext={() => navigate('/level6')} />}
      {showLosePopup && <LosePopup onReplay={() => navigate(0)} />}
    </div>
  );
};

export default Level5;